import type { SelectProps } from 'ant-design-vue'
import { message } from 'ant-design-vue'
import type { DataNode } from 'ant-design-vue/es/tree'
import type { OptionFC } from 'ant-design-vue/es/vc-select/Option'
import { isEqual, differenceWith, cloneDeep, omit } from 'lodash-es'
export const getPromise = () => {
  let resolveFn: (value: unknown) => void, rejectFn: <T>(reason?: T) => void
  const promise = new Promise((resolve, reject) => {
    resolveFn = resolve
    rejectFn = reject
  })
  return { promise, resolve: resolveFn!, reject: rejectFn! }
}
export type IKeyType = string | number
export type TTreeChild = Omit<TUser.Org['ApiGetOrgTree']['res'][number], 'child'>[]
export const getSelectItem = (key: IKeyType, title = key) => ({
  label: title,
  value: key,
  // key: key,
  // option: { label: title, value: key },
  // originLabel: title,
})

export const treeGetNode = (key: IKeyType, dicList: DataNode[]): DataNode | undefined => {
  for (let i = 0; i < dicList!.length; i++) {
    const item = dicList![i]
    if (item.key === key) return item
    if (item.children?.length) {
      const result = treeGetNode(key, item.children)
      if (result) return result
    }
  }
}

export const treeGetPathNode = <
  T extends {
    key: IKeyType
    children?: T[]
  },
>(
  key: IKeyType,
  dicList: T[],
  path: T[] = []
): T[] | undefined => {
  for (let i = 0; i < dicList!.length; i++) {
    const item = dicList![i]
    path.push(item)
    if (item.key === key) return path
    if (item.children?.length) {
      const result = treeGetPathNode(key, item.children, path)
      if (result) return result
    }
    path.pop()
  }
}

export const mergeTreeData = (tree1: DataNode[], tree2: DataNode[]) => {
  const differenceTree = differenceWith(tree2, tree1, isEqual)
  differenceTree.forEach((item) => {
    const path = treeGetPathNode(item.key, tree1)
    if (path?.length) {
      const fathKey = path.slice(-2)
      if (fathKey.length) {
        const fathItem = treeGetNode(fathKey[0].key, tree1)
        if (fathItem?.children?.length) {
          mergeTreeData(fathItem.children, [item])
        } else {
          fathItem!.children = [item]
        }
      } else {
        const index = tree1.findIndex((i) => i.key === item.key)
        if (index === -1) return
        const newItem = { ...item, ...tree1[index] }
        tree1.splice(index, 1, newItem)
      }
    } else {
      tree1.push(item)
    }
  })
}
export const treeGetItemFromSourceList = (key: IKeyType, sourceList: DataNode[][]) => {
  for (let i = 0; i < sourceList.length; i++) {
    const item = treeGetNode(key, sourceList[i])
    if (item) return item
  }
}
/**
 * 复制文本到剪贴板
 * @param text 要复制的文本
 * @returns 一个 Promise，在复制成功时解析，在复制失败时拒绝
 */
export const copyToClipboard = (text: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(text).then(resolve).catch(reject)
    } else {
      // 旧版浏览器的兼容处理
      const textArea = document.createElement('textarea')
      textArea.value = text
      textArea.style.position = 'fixed' // 避免在页面中滚动
      textArea.style.top = '0'
      textArea.style.left = '0'
      textArea.style.width = '2em'
      textArea.style.height = '2em'
      textArea.style.padding = '0'
      textArea.style.border = 'none'
      textArea.style.outline = 'none'
      textArea.style.boxShadow = 'none'
      textArea.style.background = 'transparent'
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()
      try {
        const successful = document.execCommand('copy')
        if (successful) {
          resolve()
        } else {
          reject(new Error('Failed to copy text'))
        }
      } catch (err) {
        reject(err)
      }
      document.body.removeChild(textArea)
    }
  })
}
export const renderOptions = (options: Ref<SelectProps['options']> | SelectProps['options'], Component: OptionFC) => () => {
  // 获取最新的选项列表
  const opts = isRef(options) ? options.value : options
  return opts ? (
    <>
      {opts.map((item) => (
        <Component key={item.value!} value={item.value} label={item.label} disabled={item.disabled}>
          {item.label}
        </Component>
      ))}
    </>
  ) : null
}

// 前端tree搜索
type ISearchTreeData = {
  children?: ISearchTreeData
  [propName: string]: IAcceptableType
}[]
export const searchTreeData = <T extends ISearchTreeData, K>(searchText: string | Record<string, K>, treeData: T, searchKey = 'title'): T => {
  const searchData = typeof searchText === 'string' ? { [searchKey]: searchText } : searchText
  const searchKeys = Object?.keys(searchData)
  if (!Object?.values(searchData)?.filter(Boolean)?.length) {
    return treeData
  } else {
    const filterSearch = <U extends ISearchTreeData>(tree: U): U => {
      return tree?.filter((item) => {
        const verification = searchKeys?.every((key) =>
          typeof item?.[key] === 'number' ? item?.[key] === searchData?.[key] : item?.[key]?.includes(searchData?.[key])
        )
        if (verification) return true
        if (item.children) {
          item.children = filterSearch(item?.children)
        }
        return item.children?.length
      }) as U
    }
    return filterSearch(cloneDeep(treeData))
  }
}
//前端tree搜索包含子节点
export const searchTreeDataIncludeChildren = <T extends ISearchTreeData, K>(
  searchText: string | Record<string, K>,
  treeData: T,
  searchKey = 'title'
): T => {
  const searchData = typeof searchText === 'string' ? { [searchKey]: searchText } : searchText
  const searchKeys = Object.keys(searchData)

  if (
    !Object.values(searchData).filter((item) => {
      if (item === '' || item === undefined || item === null || item === 'undefined' || item === 'null') {
        return false
      } else {
        return true
      }
    }).length
  ) {
    return treeData
  } else {
    const filterSearch = <U extends ISearchTreeData>(tree: U): U => {
      return tree
        ?.map((item) => {
          // 递归过滤子节点
          const filteredChildren = item.children ? filterSearch(item.children) : undefined

          // 检查当前节点是否匹配所有条件
          const currentNodeMatches = searchKeys.every((key) => {
            const value = item[key]
            const searchValue = searchData[key]

            // 处理布尔值比较
            if (typeof value === 'boolean' && typeof searchValue === 'boolean') {
              return value === searchValue
            }

            // 处理数字比较
            if (typeof value === 'number' && typeof searchValue === 'number') {
              return value === searchValue
            }

            // 处理字符串比较
            if (typeof value === 'string' && typeof searchValue === 'string') {
              return value.includes(searchValue)
            }

            // 默认情况下，认为不匹配
            return false
          })

          // 如果当前节点匹配所有条件，或者有匹配的子节点，则保留该节点
          if (currentNodeMatches || (filteredChildren && filteredChildren?.length > 0)) {
            return {
              ...item,
              children: filteredChildren,
            }
          }

          // 否则，不保留该节点
          return null
        })
        .filter(Boolean) as U
    }

    // 应用过滤并移除 null 节点
    const filteredTree = filterSearch(cloneDeep(treeData))
    return filteredTree as T
  }
}

/**
 * 根据分类路径查找对应的标签 处理业务属性数据
 * @param path - 分类路径数组，例如 ['1'] 或 ['1', '2']
 * @param data - 分类数据数组
 * @returns 单个标签字符串或用 '-' 拼接的标签字符串，或错误信息
 */
export const findCategoryLabels = <T extends Record<string, IAcceptableType>[] | undefined>(path: string[], data: T): string => {
  let currentLevel = data || []
  const labels: string[] = []
  for (const value of path) {
    const category = currentLevel.find((item) => item.value === value)
    if (!category) {
      return `未找到分类: ${value}`
    }
    labels.push(category.label)
    currentLevel = category.children || []
  }

  if (labels.length === 1) {
    return labels[0]
  } else if (labels.length === 2) {
    return `${labels[0]}-${labels[1]}`
  } else {
    // 如果需要支持更多级别，可以自行调整此逻辑
    return labels.join('-')
  }
}

/**
 * 列表回显
 * @param val - 传入 status的类型
 * @param data - status数据数组
 * @returns 单个标签字符串或用 '-' 拼接的标签字符串，或错误信息
 */
export const getListType = <T extends Record<string, IAcceptableType>>(val: string | undefined | number, arr: T[]) => {
  if (val || val === 0) {
    const item = arr.find((item) => item.value === val)
    return item?.label
  } else {
    return '-'
  }
}

/**
 * 解析 base64 并下载文件
 * @param base64Data base64 文件流
 * @param fileName 下载的文件名
 */
export const downloadFile = (base64Data: string, fileName: string) => {
  try {
    // 解码 Base64 字符串
    const byteArray = new Uint8Array(
      atob(base64Data)
        .split('')
        .map((char) => char.charCodeAt(0))
    )
    // 生成 Blob 对象，方便下载
    const blob = new Blob([byteArray], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
    const link = document.createElement('a')
    link.href = URL.createObjectURL(blob)
    link.download = fileName // 设置下载文件名
    link.click()
  } catch (error) {
    message.error(`下载失败:'${error}`)
  }
}

/**
 * 解析blob文件流并下载文件
 * @param binFile  文件流
 * @param fileName 下载的文件名
 */
export const downloadBinaryFile = async (binFile: string, fileName: string) => {
  // 处理二进制数据并创建 Blob 对象
  const blobObj = new Blob([binFile], { type: 'application/octet-stream' })
  // 创建一个链接并设置下载属性
  const downloadLink = document.createElement('a')
  let url = window.URL || window.webkitURL // 兼容不同浏览器的 URL 对象
  const downloadUrl = url.createObjectURL(blobObj)
  downloadLink.href = downloadUrl
  downloadLink.download = fileName // 设置下载的文件名
  // 将链接添加到 DOM 中，模拟点击
  document.body.appendChild(downloadLink)
  downloadLink.click()
  // 移除创建的链接和释放 URL 对象
  document.body.removeChild(downloadLink)
  window.URL.revokeObjectURL(downloadUrl)
}

/**
 * 将树结构转换为列表结构
 * @param tree 树
 */
export const treeToList = (tree: TUser.Org['ApiGetOrgTree']['res']): Omit<TUser.Org['ApiGetOrgTree']['res'][number], 'child'>[] => {
  let list: Omit<TUser.Org['ApiGetOrgTree']['res'][number], 'child'>[] = []
  if (!tree || tree.length === 0) return list
  for (let i: number = 0; i < tree.length; i++) {
    let node = tree[i]
    let temObj = {
      ...omit(node, 'child'),
    }
    list.push(temObj)
    if (node.child && node.child.length > 0) {
      let childList = treeToList(node.child)
      list = list.concat(childList)
    }
  }
  return list
}
